"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SubmitHandler, useForm } from "react-hook-form";
import { TrackGoogleAnalyticsEvent } from "@/google-analytics";
import { useUserContext } from "@/contexts/userContext";
import { useToast } from "../../ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useRouter } from "next/router";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Button } from "../../ui/button";
import { joinValidString, randomString } from "@/helpers/helperFunctions";
import {
  useGetCtraderSymbol,
  useGetInstrumentSuggestion,
  useGetLeverateSymbol,
  useGetMatchSymbol,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { FaAngleRight } from "react-icons/fa6";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../ui/hover-card";
import Loader from "../../Loader";
import { useTranslation } from "react-i18next";
import { ChevronDown, ExternalLinkIcon } from "lucide-react";
import { TradingViewTimeFrames } from "../../TradingViewWidgets";
import { useRecoilState } from "recoil";
import {
  selectedBrokerState,
  selectedTimeFrameState,
  setSelectedSymbolState,
} from "@/Atoms/strategy/StrategyFormAtom";
import { usePostCopyStrategy, usePostCreateStrategy } from "@/api/strategyApi";
import {
  brokerConnectModalAtom,
  guideModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import { useQuickStrategyContext } from "@/contexts/QuickStrategyContext";
import Link from "next/link";
import Fuse from "fuse.js";
import { Label } from "../../ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../../ui/command";
import { LuLoader2 } from "react-icons/lu";
import { Skeleton } from "../../ui/skeleton";
import SuggestionPopup from "../marketplace/SuggestionPopup";
import { brokersList } from "../connection/BrokerList";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import HintLabel from "../common/HintLable";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
} from "@/components/ui/alert-dialog";
import StrategyTemplate from "@/components/walk-through/explore/StrategyTemplate";


interface IFormValues {
  symbol?: string;
  timeframe?: string;
  strategy_name?: string;
}

const CreateStrategyForm = ({ disableBanner }: { disableBanner?: boolean }) => {
  // === HOOKS ===
  const router = useRouter();
  const { t } = useTranslation("build-page");
  const { toast } = useToast();
  const { user, connectedBrokers, balanceLoading } = useUserContext();
  const { subscriptionData, userType } = useSubscription();
  const { isScannerType, setQuickStrategyModalToggle } =
    useQuickStrategyContext();

  const buildForm = useForm<IFormValues>({ mode: "onSubmit" });

  const [selectedSymbol, setSelectedSymbol] = useRecoilState(
    setSelectedSymbolState,
  );
  const [selectedTimeFrame, setSelectedTimeFrame] = useRecoilState(
    selectedTimeFrameState,
  );
  const [broker, setBroker] = useRecoilState(selectedBrokerState);

  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );

  const [marketplacePopupShow, setMarketplacePopupShow] = useState(false);
  const [ftuOpen, setFtuOpen] = useState<boolean>(false);


  //New Variables
  const [trendingSymbolData, setTrendingSymbolData] = useState<any[]>([]);
  const [currentSymbolData, setcurrentSymbolData] = useState<any[]>([]);

  const [strategyNameData, setStrategyNameData] = useState({
    sym: "",
    epoc: "",
    tf: "",
  });
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [symbolData, setSymbolData] = useState<any[]>([]);
  const [instrumentInput, setInstrumentInput] = useState("");

  const fuseRef = useRef<Fuse<any> | null>(null);
  const allSymbolsEnabledRef = useRef<boolean>(false);

  const { data: trendingSymbolsData } = useGetTrendingSymbols("equity", broker);

  const { data: matchSymbolsData } = useGetMatchSymbol("forex");

  const { data: cTraderSymbolData, error: cTraderError } = useGetCtraderSymbol(
    user?.user_id,
  );

  const { data: leverateSymbolData } = useGetLeverateSymbol(
    user?.user_id,
    broker ?? "",
  );

  const { details } = useAppDetails();

  const {
    data: createStrategyData,
    mutateAsync: createStrategy,
    isSuccess: isCreateStrategySuccess,
    isPending: isCreateStrategyLoading,
  } = usePostCreateStrategy();

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const {
    data: instrumentSuggestionData,
    isLoading: isInstrumentSuggestionLoading,
  } = useGetInstrumentSuggestion(user?.user_id, undefined, broker);

  // === TIMEFRAME OPTIONS ===
  const timeframeOptions = [
    // {
    //   label: t("3 Min"),
    //   value: "3m",
    //   tradingViewValue: TradingViewTimeFrames.THREE_MINUTE,
    // },
    {
      label: t("5 Min"),
      value: "5m",
      tradingViewValue: TradingViewTimeFrames.FIVE_MINUTES,
    },
    {
      label: t("15 Min"),
      value: "15m",
      tradingViewValue: TradingViewTimeFrames.FIFTEEN_MINUTES,
    },
    {
      label: t("30 Min"),
      value: "30m",
      tradingViewValue: TradingViewTimeFrames.THIRTY_MINUTES,
    },
    {
      label: t("60 Min"),
      value: "60m",
      tradingViewValue: TradingViewTimeFrames.ONE_HOUR,
    },
  ];

  // === CALLBACKS ===
  const updateStrategyNameData = useCallback(
    (
      field: keyof typeof strategyNameData,
      value: string | null | undefined,
    ) => {
      setStrategyNameData((p) => {
        const epoc = Math.random().toString(36).substring(2, 7);
        const updated = { ...p, [field]: value, epoc };
        buildForm.setValue(
          "strategy_name",
          joinValidString([updated.sym, updated.tf, updated.epoc]),
        );
        return updated;
      });
    },
    [],
  );

  const clearFormSymbol = useCallback(() => {
    buildForm.setValue("symbol", undefined);
    updateStrategyNameData("sym", "");
  }, [updateStrategyNameData]);

  const onBuildFormSubmit: SubmitHandler<IFormValues> = async (values) => {
    if (!user) return;
    if (isScannerType) {
      await copyStrategy({
        userId: user?.user_id,
        key: "0cea3887-6801-4db2-99ee-4e0ddf2c716f",
        symbol: values?.symbol,
      });
    } else {
      try {
        await createStrategy({
          symbol: values?.symbol,
          timeframe: values?.timeframe,
          strategy_name: values?.strategy_name,
          user_id: user.user_id ?? "",
          broker_id: broker == "100" ? "4" : (broker ?? ""),
        });
        clearFormSymbol();
        setSelectedTimeFrame(undefined);
      } catch (e) {
        toast({ title: "Error creating strategy", variant: "destructive" });
      }
    }
  };

  useEffect(() => {
    clearFormSymbol();
    setSelectedSymbol(undefined);
    setMarketplacePopupShow(false);
  }, [broker]);

  useEffect(() => {
    if (cTraderError && broker == "2" && showSuggestion) {
      setShowConnectForm({ isOpen: true, brokerId: "2" });
      toast({
        title: (cTraderError as any)?.response?.data?.detail,
        variant: "default",
      });
    }
  }, [showSuggestion]);

  // === EFFECTS ===

  useEffect(() => {
    if (isCreateStrategySuccess && createStrategyData?.data?.strategy) {
      TrackGoogleAnalyticsEvent(
        "strategy_created",
        "strategy_created",
        "strategy_created",
      );
      const _url = new URL(window.location.href);
      const locale = _url.searchParams.get("locale") || "en";
      router.replace(
        `/strategy/${createStrategyData.data.strategy.id}/${
          createStrategyData.data.strategy.keygen
        }/false?ref=${encodeURIComponent(
          _url.pathname + _url.search,
        )}&locale=${locale}&scanner=${isScannerType}`,
      );
    } else if (createStrategyData?.data?.detail) {
      toast({ title: createStrategyData.data.detail, variant: "destructive" });
    }
  }, [isCreateStrategySuccess]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData) {
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id ?? "") +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user?.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`,
        );
      }, 1000);
    }
  }, [isCopySuccess, isCopyPending]);

  useEffect(() => {
    allSymbolsEnabledRef.current = true;
    setSymbolData([]);
    updateStrategyNameData("sym", "");
  }, [subscriptionData, userType]);

  useEffect(() => {
    if (symbolData?.length) {
      fuseRef.current = new Fuse(symbolData, {
        keys: ["symbol", "name", "short_name"],
        threshold: 0.3,
      });
    }
  }, [symbolData]);

  //Trending symbols effect
  useEffect(() => {
    if ([details?.id].includes(broker ?? "") && leverateSymbolData) {
      setTrendingSymbolData(
        leverateSymbolData.data.slice(0, 5).map((item: string) => ({
          short_name: "prop",
          symbol: item,
          name: item,
        })),
      );
    } else if (broker && trendingSymbolsData) {
      setTrendingSymbolData(
        Object.entries(trendingSymbolsData.data)
          .slice(0, 5)
          .map(([symbol, name]) => ({
            name,
            symbol,
            short_name: symbol,
          })),
      );
    }
  }, [
    broker,
    trendingSymbolsData,
    matchSymbolsData,
    cTraderSymbolData,
    leverateSymbolData,
    connectedBrokers,
  ]);

  useEffect(() => {
    if (!details?.isWhiteLabled) return;
    setcurrentSymbolData(
      leverateSymbolData?.data.map((item: string) => ({
        short_name: "asset",
        symbol: item,
        name: item,
      })),
    );
  }, [leverateSymbolData]);

  //Current symbols effect
  useEffect(() => {
    if (instrumentSuggestionData) {
      setcurrentSymbolData([...instrumentSuggestionData.data]);
    }
  }, [
    broker,
    trendingSymbolsData,
    matchSymbolsData,
    cTraderSymbolData,
    instrumentSuggestionData,
    leverateSymbolData,
  ]);

  useEffect(() => {
    if (Object.keys(connectedBrokers).length == 0 || details?.isWhiteLabled) {
      setBroker(details?.id);
    } else {
      setBroker(
        brokersList?.filter(
          (broker) => connectedBrokers[Number(broker.id)],
        )?.[0]?.id,
      );
    }
  }, [connectedBrokers, balanceLoading]);

  const handleSymbolInput = (symbol: string) => {
    setInstrumentInput(symbol);
  };
  const symbolList = instrumentInput ? currentSymbolData : trendingSymbolData;

  // ✅ Create Fuse instance once (memoized)
  const fuse = useMemo(() => {
    return new Fuse(symbolList, {
      keys: ["symbol", "name"], // searchable fields
      threshold: 0.3, // lower = stricter matching
      minMatchCharLength: 1,
      ignoreLocation: true,
    });
  }, [
    symbolList,
    currentSymbolData,
    trendingSymbolData,
    instrumentInput,
    broker,
    connectedBrokers,
  ]);

  // ✅ Perform search (with slice limit)
  const results = useMemo(() => {
    try {
      if (!instrumentInput?.trim() && symbolList)
        return symbolList?.slice(0, 10) || [];
      if (!fuse) return [];
      return fuse
        ?.search(instrumentInput)
        ?.slice(0, 5)
        .map((res) => res.item);
    } catch (error) {
      console.log("error", trendingSymbolData, currentSymbolData);
      return [];
    }
  }, [instrumentInput, fuse, symbolList, broker, connectedBrokers]);

  // === RETURN ===

  return (
    <>
      <Form {...buildForm}>
        <form
          method="post"
          onSubmit={buildForm.handleSubmit(onBuildFormSubmit)}
          id="strategy-form-1"
          className="relative"
        >
        {marketplacePopupShow && strategyNameData?.sym && !isScannerType && (
          <div className="absolute top-[10%] -right-[65%] mt-2 w-64 z-20">
            <SuggestionPopup
              show={marketplacePopupShow}
              setShow={setMarketplacePopupShow}
              symbol={strategyNameData?.sym}
            />
          </div>
        )}

        <CardHeader className=" flex flex-col gap-0 pb-6 pt-8">
          <CardTitle className="flex flex-col">
            <div className="flex gap-2 items-center text-2xl font-bold tracking-tight">
              {isScannerType
                ? t("Create a Scanner Strategy")
                : t("Create a Strategy")}
            </div>
            <p className="text-sm text-muted-foreground font-medium tracking-normal">
              {t("Build your own with simple visual blocks")}
            </p>
          </CardTitle>
        </CardHeader>

        <CardContent className={""}>
          <div className="flex flex-col mt-0 gap-5 cursor-pointer">
            {!details?.isWhiteLabled && (
              <div className="flex flex-col gap-2">
                <HintLabel
                  className=" flex flex-row gap-0 items-center"
                  label={t("Select Broker")}
                  hint={
                    Object.keys(connectedBrokers).length === 0 &&
                    !balanceLoading &&
                    t(
                      "Connect your broker account to access live market data and deploy  strategies for fully automated trading",
                    )
                  }
                ></HintLabel>

                {balanceLoading ? (
                  <Skeleton className="h-10"></Skeleton>
                ) : (
                  <HoverCard openDelay={100} closeDelay={100}>
                    <Select onValueChange={(e) => setBroker(e)} value={broker}>
                      <SelectTrigger className="text-[--accent-primary] border-[--accent-primary] focus:ring-0">
                        <SelectValue placeholder={t("Select Broker")} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(connectedBrokers).length > 0 ? (
                          // 🔹 Show connected brokers only
                          brokersList
                            ?.filter(
                              (broker) => connectedBrokers[Number(broker.id)],
                            )
                            .map((broker) => (
                              <SelectItem
                                hideCheck
                                value="100"
                                className="flex items-center gap-2"
                              >
                                <div className="flex items-center gap-2">
                                  <img
                                    src={details?.brokerIcon}
                                    alt="Explore"
                                    className="h-[25px] aspect-square"
                                  />
                                  <p>{t("Explore with FREE market data")}</p>
                                </div>
                              </SelectItem>
                            ))
                        ) : (
                          <HoverCardTrigger asChild>
                            <SelectItem
                              hideCheck
                              value="4"
                              className="flex items-center gap-2 flex"
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  src={details?.brokerIcon}
                                  alt="Explore"
                                  className="h-[25px]"
                                />
                                <p>{t("Explore with FREE market data")}</p>
                              </div>
                            </SelectItem>
                          </HoverCardTrigger>
                        )}
                      </SelectContent>
                    </Select>
                    {Object.keys(connectedBrokers).length == 0 && (
                      <HoverCardContent
                        className="w-[280px] text-sm text-muted-foreground"
                        align="start"
                      >
                        <p>
                          {t(
                            `Just exploring? Experiment with free U.S. equities data. Connect your broker account to deploy strategies for automated trading.`,
                          )}
                        </p>
                      </HoverCardContent>
                    )}
                  </HoverCard>
                )}
              </div>
            )}

            <FormField
              control={buildForm.control}
              name="symbol"
              defaultValue={selectedSymbol}
              rules={{ required: t("Asset is required.") }}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("Asset Name")}</FormLabel>

                  <Popover
                    open={showSuggestion}
                    onOpenChange={setShowSuggestion}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          onClick={() => {
                            setInstrumentInput("");
                          }}
                          className="w-full justify-between text-[--accent-primary] border-[--accent-primary] focus:ring-0"
                        >
                          {field.value && selectedSymbol
                            ? selectedSymbol
                            : t("Select symbol")}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>

                    <PopoverContent
                      side="bottom"
                      className="w-[var(--radix-popover-trigger-width)] p-0"
                    >
                      <Command
                        filter={() => 1} // Always match, disables built-in filtering
                      >
                        <CommandInput
                          value={instrumentInput}
                          onValueChange={handleSymbolInput}
                          placeholder={t("Search symbol")}
                        />
                        {!isInstrumentSuggestionLoading && (
                          <CommandEmpty>{t("No symbol found.")}</CommandEmpty>
                        )}

                        {broker &&
                        !instrumentSuggestionData &&
                        instrumentInput ? (
                          <div className="bg-background flex items-center justify-center p-4">
                            <LuLoader2 className="animate-spin ml-1" />
                          </div>
                        ) : (
                          <CommandGroup>
                            {results?.map((sym) => {
                              return (
                                <CommandItem
                                  key={sym?.symbol}
                                  value={sym?.symbol}
                                  onSelect={() => {
                                    field.onChange(sym.symbol);
                                    setSelectedSymbol(sym?.symbol);
                                    updateStrategyNameData("sym", sym?.symbol);
                                    setShowSuggestion(false);
                                    setMarketplacePopupShow(true);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <p> {sym?.symbol}</p>
                                    <p className="text-muted-foreground">
                                      {" "}
                                      {sym?.name}
                                    </p>
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        )}
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={buildForm.control}
              name="timeframe"
              rules={{ required: t("Timeframe is required.") }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Timeframe")}</FormLabel>
                  <Select
                    onValueChange={(select?: string) => {
                      const _value = timeframeOptions.find(
                        (inst) => inst.value === select,
                      );
                      _value && setSelectedTimeFrame(_value);

                      // Correctly update form state
                      field.onChange(select || undefined);

                      updateStrategyNameData("tf", select);
                    }}
                    defaultValue={field.value}
                    name="timeframe"
                    value={selectedTimeFrame?.value}
                  >
                    <FormControl>
                      <SelectTrigger className="text-[--accent-primary] border-[--accent-primary] focus:ring-0">
                        <SelectValue placeholder={t("Select timeframe")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent side="bottom">
                      {timeframeOptions.map((option, index) => (
                        <SelectItem
                          value={option.value}
                          key={`option-${index}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {
              <Card
                onClick={() => {
                  setFtuOpen(true);
                }}
                className="bg-muted p-2 flex-col gap-1 flex cursor-pointer hover:bg-muted/80 transition-colors"
              >
                <CardTitle className="text-base">
                  {t("New to Algo Studio?")}
                </CardTitle>
                <CardDescription className="text-xs font-bold">
                  {t(
                    "Choose a symbol and timeframe to start building your automated trading strategy or jump straight in with a ready-made template. Click here to get started.",
                  )}
                </CardDescription>
              </Card>
            }
          </div>
        </CardContent>
        <CardFooter className="flex items-center   flex-col mt-8 ">
          <div className="w-full flex justify-end">
            <Button
              loading={isCopyPending || isCreateStrategyLoading}
              type="submit"
              className="flex gap-2 font-bold"
              disabled={isCreateStrategyLoading}
            >
              {t("Build Strategy")}
              {isCreateStrategyLoading ? <Loader /> : <FaAngleRight />}
            </Button>
          </div>
          <div className="flex mt-5 text-[--accent-secondary] font-bold  items-center justify-around border-t-2 pt-2 w-full  ">
            <div className=" flex cursor-pointer flex-col justify-end items-end ">
              <Link
                href={"/test"}
                onClick={() => {
                  setQuickStrategyModalToggle(false);
                }}
                className="flex text-sm hover:underline text-[var(--accent-primary)] font-medium gap-2 items-center"
              >
                <div>
                  <p className="">{t("View created")} </p>
                  <p>{t("strategies")}</p>
                </div>
                <ExternalLinkIcon className="h-[15px]" />
              </Link>
            </div>
            <div className="w-px bg-muted-foreground/25 h-12 self-center"></div>{" "}
            {/* Vertical Line */}
            <div className=" flex cursor-pointer  flex-col justify-end items-end ">
              <Link
                href={"/deploy"}
                onClick={() => {
                  setQuickStrategyModalToggle(false);
                }}
                className="flex text-sm hover:underline text-[var(--accent-primary)] font-medium gap-2 items-center"
              >
                <div>
                  <p className="">{t("View deployed")} </p>
                  <p>{t("strategies")}</p>
                </div>
                <ExternalLinkIcon className="h-[15px]" />
              </Link>
            </div>
          </div>
        </CardFooter>
      </form>
    </Form>

    <AlertDialog open={ftuOpen} onOpenChange={(open) => setFtuOpen(open)}>
      <AlertDialogContent className="max-w-5xl px-3 ">
        <div className="w-full flex flex-col">
          <StrategyTemplate onSelect={() => setFtuOpen(false)} />
          <div className="w-full flex justify-end mt-2">
            <AlertDialogCancel>
              <>{t("Close") || "Close"}</>
            </AlertDialogCancel>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default CreateStrategyForm;
